import React, { useState, useEffect, useRef } from "react";
import {
  motion,
  useAnimation,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { useInView } from "react-intersection-observer";
import styled from "styled-components";
import {
  FaCalendarAlt,
  FaLaptopMedical,
  FaHeadset,
  FaArrowRight,
  FaArrowDown,
  FaUser,
  FaPlus,
  FaMinus,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaArrowUp,
} from "react-icons/fa";
import { db } from "../components/firebase-config.js";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  Timestamp,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Quick from "./Quick.js";

// Composant principal
const SenAssistanceMedicaleDigitale = () => {
  const [activeService, setActiveService] = useState(null);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeGalleryImage, setActiveGalleryImage] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);
  const [userType, setUserType] = useState("professionnel");
  const [testimonials, setTestimonials] = useState([]);
  const [isTestimonialFormOpen, setIsTestimonialFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTestimonial, setNewTestimonial] = useState({
    text: "",
    author: "",
    role: "",
    type: "professionnel",
  });
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.2 });

  // Références pour le système d'ancrage
  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const servicesRef = useRef(null);
  const testimonialsRef = useRef(null);
  const galleryRef = useRef(null);
  const faqRef = useRef(null);
  const contactRef = useRef(null);

  const navigate = useNavigate();

  // Effet de parallaxe pour la section héro
  const { scrollY } = useScroll();
  const heroImageY = useTransform(scrollY, [0, 500], [0, 100]);
  const heroTextY = useTransform(scrollY, [0, 500], [0, -50]);

  const [showAllTestimonials, setShowAllTestimonials] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    structure: "",
    pack: "",
    service: "",
    message: "",
    type: "professionnel",
  });

  // États pour la vérification des demandes
  const [isVerificationOpen, setIsVerificationOpen] = useState(false);
  const [verificationData, setVerificationData] = useState({
    searchType: "phone",
    value: "",
  });
  const [verificationResults, setVerificationResults] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  // Fonction pour faire défiler vers une section
  const scrollToSection = (ref) => {
    ref.current.scrollIntoView({ behavior: "smooth" });
  };

  // Fonction pour gérer la sélection d'un pack
  const handlePackSelection = (packName) => {
    setSelectedPack(packName);
    setActiveService(null);
    scrollToSection(contactRef);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 1.2,
      },
    },
  };

  const staggerContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const galleryItemVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
    hover: {
      scale: 1.05,
      boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
      },
    },
  };

  // Services de l'entreprise
  const services = [
    {
      id: 1,
      title: "Secrétariat Virtuel",
      icon: <FaHeadset size={40} />,
      description:
        "Notre service de secrétariat virtuel est spécialement conçu pour les professionnels de santé. Une équipe de secrétaires médicales qualifiées prend en charge la gestion administrative de votre cabinet ou clinique, vous permettant de vous concentrer pleinement sur vos patients.",
      features: [
        "Gestion des appels et prise de rendez-vous",
        "Accueil et orientation des patients",
        "Suivi des dossiers médicaux",
        "Gestion des urgences médicales",
        "Coordination avec les laboratoires et pharmacies",
        "Support administratif 24/7",
      ],
      color: "#009eff",
    },
    {
      id: 2,
      title: "Prise de Rendez-vous",
      icon: <FaCalendarAlt size={40} />,
      description:
        "Notre système intelligent de prise de rendez-vous optimise votre planning et réduit les désistements. Les patients peuvent réserver, modifier ou annuler leurs rendez-vous en ligne à tout moment.",
      features: [
        "Réservation en ligne 24/7",
        "Rappels automatiques",
        "Gestion des annulations",
        "Synchronisation multi-plateformes",
      ],
      color: "#10B981",
      redirectTo: "/quick", 
    },
    {
      id: 3,
      title: "Digitalisation du Système de Santé",
      icon: <FaLaptopMedical size={40} />,
      description:
        "Transformez votre structure médicale grâce à notre solution complète de digitalisation. Nous proposons plusieurs packs adaptés à vos besoins, de l'essentiel au Téranga, pour vous accompagner dans votre transition numérique.",
      features: [
        "Dossiers médicaux électroniques",
        "Téléconsultation",
        "Prescription électronique",
        "Analyse de données et statistiques",
      ],
      color: "#EC4899",
      packs: [
        {
          name: "Pack Essentiel",
          features: [
            "Dossiers médicaux électroniques",
            "Gestion des rendez-vous",
            "Facturation électronique",
            "Support technique standard",
          ],
        },
        {
          name: "Pack Avancé",
          features: [
            "Toutes les fonctionnalités du Pack Essentiel",
            "Téléconsultation",
            "Prescription électronique",
            "Support technique prioritaire",
          ],
        },
        {
          name: "Pack Téranga",
          features: [
            "Toutes les fonctionnalités du Pack Avancé",
            "Analyse de données et statistiques",
            "Application web  personnalisée",
            "Formation complète du personnel",
            "Support technique dédié 24/7",
          ],
        },
      ],
    },
  ];

  // FAQ items
  const faqItems = [
    {
      id: 1,
      question: "Comment fonctionne le service de secrétariat virtuel ?",
      answer:
        "Notre service de secrétariat virtuel est assuré par des professionnels formés qui prennent en charge vos appels téléphoniques, la gestion de votre agenda, le traitement des emails et l'organisation administrative. Nous mettons en place un numéro dédié ou redirigeons vos appels vers notre plateforme. Toutes les informations sont accessibles en temps réel via notre interface sécurisée.",
    },
    {
      id: 2,
      question:
        "Est-ce que les patients peuvent prendre rendez-vous en dehors des heures d'ouverture ?",
      answer:
        "Absolument ! Notre système de prise de rendez-vous en ligne est disponible 24h/24 et 7j/7. Les patients peuvent réserver, modifier ou annuler leurs rendez-vous à tout moment selon les plages horaires que vous aurez définies. Vous gardez un contrôle total sur votre planning tout en offrant plus de flexibilité à vos patients.",
    },
    {
      id: 3,
      question:
        "La solution est-elle conforme aux réglementations sur les données de santé ?",
      answer:
        "Oui, notre solution est entièrement conforme aux réglementations en vigueur concernant la protection des données de santé. Nous utilisons des protocoles de chiffrement avancés, des serveurs sécurisés et des procédures strictes d'accès aux données. Toutes nos solutions respectent les normes internationales de protection des données médicales.",
    },
    {
      id: 4,
      question:
        "Combien de temps faut-il pour mettre en place votre solution dans notre structure ?",
      answer:
        "Le temps de déploiement dépend de la taille de votre structure et des modules choisis. En général, notre solution peut être opérationnelle en 2 à 4 semaines. Nous commençons par une phase d'analyse de vos besoins, suivie de la configuration du système, puis de la formation de votre personnel. Un accompagnement continu est assuré pendant les premières semaines d'utilisation.",
    },
    {
      id: 5,
      question: "Proposez-vous des formations pour notre personnel ?",
      answer:
        "Oui, nous proposons des sessions de formation complètes pour votre personnel. Ces formations sont incluses dans notre offre et peuvent être dispensées sur site ou à distance. Nous fournissons également une documentation détaillée et des tutoriels vidéo accessibles à tout moment via notre plateforme.",
    },
  ];

  // Images de la galerie
  const galleryImages = [
    {
      id: 1,
      url: "https://i.pinimg.com/originals/72/54/a6/7254a654679cad64c2ad0658e1556a93.gif",
      title: "Interface de prise de rendez-vous",
      description:
        "Notre interface intuitive permet aux patients de réserver facilement leurs consultations",
    },
    {
      id: 2,
      url: "https://i.pinimg.com/736x/74/b0/dc/74b0dc438220500be3dee252b0dcfe21.jpg",
      title: "Équipe médicale utilisant notre solution",
      description:
        "Des professionnels de santé satisfaits de notre plateforme digitale",
    },

    {
      id: 3,
      url: "https://i.pinimg.com/originals/89/f6/d5/89f6d54b09cad26da2911701e5fce3c1.gif",
      title: "Tableau de bord analytique",
      description:
        "Visualisez les statistiques de votre structure médicale en temps réel",
    },
    {
      id: 4,
      url: "https://i.pinimg.com/originals/2e/ec/aa/2eecaaba987a20a5b0eb6a8b960fdf43.gif",
      title: "Sécurité des données",
      description:
        "Infrastructure sécurisée pour la protection des données sensibles",
    },
    {
      id: 5,
      url: "https://i.pinimg.com/originals/de/01/e7/de01e79dd4c5a4a07ba9ede68dc62486.gif",
      title: "Formation du personnel",
      description:
        "Sessions de formation pour une prise en main rapide de nos solutions",
    },
  ];

  // Charger les témoignages depuis Firebase
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setIsLoading(true);
        const now = Timestamp.now();
        const testimonialsRef = collection(db, "testimonials");
        const q = query(
          testimonialsRef,
          where("expiresAt", ">", now),
          orderBy("expiresAt", "desc")
        );

        const querySnapshot = await getDocs(q);
        const testimonialsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setTestimonials(testimonialsData);
        setError(null);
      } catch (err) {
        console.error("Erreur lors du chargement des témoignages:", err);
        setError(
          "Impossible de charger les témoignages. Veuillez réessayer plus tard."
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  // Fonction pour ajouter un nouveau témoignage
  const handleAddTestimonial = async (e) => {
    e.preventDefault();
    try {
      const now = Timestamp.now();
      const oneYearLater = new Date(now.toDate());
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);

      const testimonialData = {
        text: newTestimonial.text,
        author: newTestimonial.author,
        role: newTestimonial.role,
        type: newTestimonial.type,
        date: now,
        expiresAt: Timestamp.fromDate(oneYearLater),
      };

      const docRef = await addDoc(
        collection(db, "testimonials"),
        testimonialData
      );

      // Ajouter le nouveau témoignage à l'état local
      setTestimonials((prev) => [
        {
          id: docRef.id,
          ...testimonialData,
        },
        ...prev,
      ]);

      // Réinitialiser le formulaire
      setNewTestimonial({
        text: "",
        author: "",
        role: "",
        type: "professionnel",
      });
      setIsTestimonialFormOpen(false);
    } catch (err) {
      console.error("Erreur lors de l'ajout du témoignage:", err);
      setError(
        "Impossible d'ajouter le témoignage. Veuillez réessayer plus tard."
      );
    }
  };

  // Fonction pour obtenir les témoignages à afficher
  const getDisplayedTestimonials = () => {
    return showAllTestimonials ? testimonials : testimonials.slice(0, 3);
  };

  const handleServiceClick = (service) => {
    if (service.redirectTo) {
      navigate(service.redirectTo); // Utilisation du hook navigate pour la redirection
    } else {
      setActiveService(service); // Comportement par défaut pour les autres services
    }
  };

  
  // Fonction pour gérer la soumission du formulaire de contact
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const contactData = {
        ...contactForm,
        createdAt: Timestamp.now(),
        status: "pending", // pending, processed, rejected
      };

      await addDoc(collection(db, "contacts"), contactData);

      // Réinitialiser le formulaire
      setContactForm({
        name: "",
        email: "",
        phone: "",
        structure: "",
        pack: "",
        service: "",
        message: "",
        type: "professionnel",
      });

      setSubmitSuccess(true);
    } catch (err) {
      console.error("Erreur lors de l'envoi du formulaire:", err);
      setSubmitError(
        "Une erreur est survenue lors de l'envoi du formulaire. Veuillez réessayer."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour vérifier le statut d'une demande
  const handleVerifyRequest = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    setVerificationError(null);
    setVerificationResults(null);

    try {
      const contactsRef = collection(db, "contacts");
      let q;

      if (verificationData.searchType === "phone") {
        q = query(contactsRef, where("phone", "==", verificationData.value));
      } else {
        q = query(contactsRef, where("name", "==", verificationData.value));
      }

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setVerificationError("Aucune demande trouvée avec ces informations.");
      } else {
        const results = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc
            .data()
            .createdAt?.toDate()
            .toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }),
        }));
        setVerificationResults(results);
      }
    } catch (err) {
      console.error("Erreur lors de la vérification:", err);
      setVerificationError(
        "Une erreur est survenue lors de la vérification. Veuillez réessayer."
      );
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Container>
      {/* Navigation fixe */}
      <Navbar>
        <NavLogo>
          <Logo>SAMD</Logo>
        </NavLogo>

        <NavLinks>
          <NavLink onClick={() => scrollToSection(heroRef)}>Accueil</NavLink>
          <NavLink onClick={() => scrollToSection(aboutRef)}>À propos</NavLink>
          <NavLink onClick={() => scrollToSection(servicesRef)}>
            Services
          </NavLink>
          <NavLink onClick={() => scrollToSection(galleryRef)}>Galerie</NavLink>
          <NavLink onClick={() => scrollToSection(faqRef)}>FAQ</NavLink>
          <NavLink onClick={() => scrollToSection(contactRef)}>Contact</NavLink>
        </NavLinks>

        <NavLoginButton onClick={() => navigate("/Auth")}>
          <FaUser style={{ marginRight: "8px" }} />
        </NavLoginButton>
      </Navbar>

      {/* Section Hero */}
      <HeroSection ref={heroRef}>
        <motion.div
          style={{ y: heroTextY }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroTitle>
            Révolutionnez votre pratique médicale
            <HeroHighlight> grâce au numérique</HeroHighlight>
          </HeroTitle>

          <HeroDescription>
            Des solutions innovantes pour digitaliser et optimiser la gestion de
            votre structure de santé au Sénégal
          </HeroDescription>

          <ButtonGroup>
            <PrimaryButton onClick={() => scrollToSection(servicesRef)}>
              Découvrir nos services
              <FaArrowRight style={{ marginLeft: "10px" }} />
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate("/quick")}>
              Prendre rendez-vous
            </SecondaryButton>
          </ButtonGroup>
        </motion.div>

        <HeroImageWrapper>
          <motion.div
            style={{ y: heroImageY }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          ></motion.div>
        </HeroImageWrapper>

        <ScrollIndicator onClick={() => scrollToSection(aboutRef)}>
          <motion.div
            animate={{
              y: [0, 10, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatType: "loop",
            }}
          >
            <FaArrowDown />
          </motion.div>
        </ScrollIndicator>
      </HeroSection>

      {/* Section À propos */}
      <AboutSection ref={aboutRef}>
        <SectionTitle>
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Notre Mission
          </motion.span>
        </SectionTitle>

        <AboutContent>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <AboutImage
              src="https://images.unsplash.com/photo-1584982751601-97dcc096659c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
              alt="Medical Team"
            />
          </motion.div>

          <AboutText>
            <motion.h3
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Faciliter l'accès aux soins de santé au Sénégal
            </motion.h3>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Chez <strong>Sen Assistance Médicale Digitale</strong>, nous
              croyons que la technologie peut transformer positivement le
              secteur de la santé au Sénégal. Notre équipe d'experts en santé
              numérique développe des solutions innovantes pour améliorer
              l'efficacité des structures médicales, optimiser le temps des
              praticiens et faciliter l'accès aux soins pour tous les patients.
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Fondée en 2020, notre entreprise accompagne déjà plus de 9
              structures médicales dans leur transition numérique, avec des
              résultats probants : réduction des délais d'attente, meilleure
              gestion administrative et satisfaction accrue des patients.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <AboutButton onClick={() => scrollToSection(servicesRef)}>
                Découvrir nos services
                <FaArrowRight style={{ marginLeft: "10px" }} />
              </AboutButton>
            </motion.div>
          </AboutText>
        </AboutContent>
      </AboutSection>

      {/* Section Services */}
      <ServicesSection ref={servicesRef}>
        <SectionTitle>
          <motion.span
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            Nos Services
          </motion.span>
        </SectionTitle>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="services-container"
        >
        <ServicesGrid>
  {services.map((service) => (
    <ServiceCard
      key={service.id}
      variants={itemVariants}
      whileHover={{
        y: -10,
        boxShadow:
          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }}
      transition={{ duration: 0.3 }}
      onClick={() => handleServiceClick(service)} 
    >
                <ServiceIconWrapper
                  style={{ backgroundColor: `${service.color}20` }}
                >
                  <ServiceIcon style={{ color: service.color }}>
                    {service.icon}
                  </ServiceIcon>
                </ServiceIconWrapper>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>
                  {service.description.substring(0, 120)}...
                </ServiceDescription>
                <LearnMore>
                  En savoir plus <FaArrowRight style={{ marginLeft: "5px" }} />
                </LearnMore>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </motion.div>
      </ServicesSection>

      {/* Section Témoignages */}
      <TestimonialsSection ref={testimonialsRef}>
        <SectionTitle>
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Ils nous font confiance
          </motion.span>
        </SectionTitle>

        {isLoading ? (
          <LoadingMessage>Chargement des témoignages...</LoadingMessage>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : testimonials.length === 0 ? (
          <NoTestimonialsMessage>
            Soyez le premier à partager votre expérience !
          </NoTestimonialsMessage>
        ) : (
          <>
            <TestimonialsSlider>
              {getDisplayedTestimonials().map((testimonial) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <Testimonial>
                    <TestimonialContent>
                      <QuoteIcon>"</QuoteIcon>
                      <TestimonialText>{testimonial.text}</TestimonialText>
                      <TestimonialAuthor>
                        {testimonial.author}
                      </TestimonialAuthor>
                      <TestimonialRole>{testimonial.role}</TestimonialRole>
                      <TestimonialType>{testimonial.type}</TestimonialType>
                    </TestimonialContent>
                  </Testimonial>
                </motion.div>
              ))}
            </TestimonialsSlider>

            {testimonials.length > 3 && (
              <ViewMoreButton
                onClick={() => setShowAllTestimonials(!showAllTestimonials)}
              >
                {showAllTestimonials
                  ? "Voir moins"
                  : "Voir plus de témoignages"}
              </ViewMoreButton>
            )}
          </>
        )}

        <AddTestimonialButton onClick={() => setIsTestimonialFormOpen(true)}>
          Partager votre expérience
        </AddTestimonialButton>

        <AnimatePresence>
          {isTestimonialFormOpen && (
            <Modal
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsTestimonialFormOpen(false)}
            >
              <TestimonialModalContent
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -50, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <CloseButton onClick={() => setIsTestimonialFormOpen(false)}>
                  ×
                </CloseButton>
                <TestimonialForm onSubmit={handleAddTestimonial}>
                  <h3>Partagez votre expérience</h3>

                  <FormField>
                    <FormLabel>Type d'utilisateur</FormLabel>
                    <FormSelect
                      value={newTestimonial.type}
                      onChange={(e) =>
                        setNewTestimonial({
                          ...newTestimonial,
                          type: e.target.value,
                        })
                      }
                    >
                      <option value="professionnel">
                        Professionnel de santé
                      </option>
                      <option value="structure">Structure médicale</option>
                    </FormSelect>
                  </FormField>

                  <FormField>
                    <FormLabel>Votre nom</FormLabel>
                    <FormInput
                      type="text"
                      value={newTestimonial.author}
                      onChange={(e) =>
                        setNewTestimonial({
                          ...newTestimonial,
                          author: e.target.value,
                        })
                      }
                      placeholder="Votre nom"
                      required
                    />
                  </FormField>

                  <FormField>
                    <FormLabel>Votre fonction</FormLabel>
                    <FormInput
                      type="text"
                      value={newTestimonial.role}
                      onChange={(e) =>
                        setNewTestimonial({
                          ...newTestimonial,
                          role: e.target.value,
                        })
                      }
                      placeholder="Votre fonction ou établissement"
                      required
                    />
                  </FormField>

                  <FormField>
                    <FormLabel>Votre témoignage</FormLabel>
                    <FormTextarea
                      value={newTestimonial.text}
                      onChange={(e) =>
                        setNewTestimonial({
                          ...newTestimonial,
                          text: e.target.value,
                        })
                      }
                      placeholder="Partagez votre expérience avec SAMD..."
                      required
                    />
                  </FormField>

                  <CTAButton type="submit">Publier mon témoignage</CTAButton>
                </TestimonialForm>
              </TestimonialModalContent>
            </Modal>
          )}
        </AnimatePresence>
      </TestimonialsSection>

      {/* Section Galerie */}
      <GallerySection ref={galleryRef}>
        <SectionTitle>
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Galerie
          </motion.span>
        </SectionTitle>

        <motion.div
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <GalleryGrid>
            {galleryImages.map((image) => (
              <motion.div
                key={image.id}
                variants={galleryItemVariants}
                whileHover="hover"
                onClick={() => setActiveGalleryImage(image)}
              >
                <GalleryItem>
                  <GalleryImage src={image.url} alt={image.title} />
                  <GalleryOverlay>
                    <GalleryTitle>{image.title}</GalleryTitle>
                  </GalleryOverlay>
                </GalleryItem>
              </motion.div>
            ))}
          </GalleryGrid>
        </motion.div>
      </GallerySection>

      {/* Section FAQ */}
      <FAQSection ref={faqRef}>
        <SectionTitle>
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Questions fréquentes
          </motion.span>
        </SectionTitle>

        <FAQContainer>
          {faqItems.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: item.id * 0.1 }}
            >
              <FAQItem isActive={expandedFaq === item.id}>
                <FAQQuestion
                  onClick={() =>
                    setExpandedFaq(expandedFaq === item.id ? null : item.id)
                  }
                >
                  <span>{item.question}</span>
                  <FAQIcon>
                    {expandedFaq === item.id ? <FaMinus /> : <FaPlus />}
                  </FAQIcon>
                </FAQQuestion>
                <AnimatePresence>
                  {expandedFaq === item.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FAQAnswer>{item.answer}</FAQAnswer>
                    </motion.div>
                  )}
                </AnimatePresence>
              </FAQItem>
            </motion.div>
          ))}
        </FAQContainer>
      </FAQSection>

      {/* Section CTA */}
      <CTASection ref={contactRef}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <CTAContent>
            <CTATitle>Prêt à transformer votre structure médicale ?</CTATitle>
            <CTADescription>
              Contactez-nous dès aujourd'hui pour une démonstration gratuite de
              nos solutions
            </CTADescription>

            {submitSuccess ? (
              <div>
                <SuccessMessage>
                  Merci pour votre message ! Nous vous contacterons dans les
                  plus brefs délais.
                </SuccessMessage>
                <p
                  style={{
                    marginTop: "1rem",
                    textAlign: "center",
                    color: "white",
                  }}
                >
                  Vous pouvez vérifier l'état de votre demande ultérieurement
                  avec votre numéro de téléphone ou votre nom.
                </p>
                <SecondaryButton
                  onClick={() => setIsVerificationOpen(true)}
                  style={{ margin: "1rem auto", display: "block" }}
                >
                  Vérifier ma demande
                </SecondaryButton>
              </div>
            ) : (
              <ContactForm onSubmit={handleContactSubmit}>
                <FormRow>
                  <FormField>
                    <FormLabel>Type d'utilisateur</FormLabel>
                    <FormSelect
                      value={contactForm.type}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, type: e.target.value })
                      }
                    >
                      <option value="professionnel">
                        Professionnel de santé
                      </option>
                      <option value="structure">Structure médicale</option>
                    </FormSelect>
                  </FormField>
                </FormRow>

                {contactForm.type === "particulier" && (
                  <>
                    <FormRow>
                      <FormField>
                        <FormLabel>Numéro de téléphone (Obligatoire)</FormLabel>
                        <FormInput
                          type="tel"
                          placeholder="Votre numéro de téléphone"
                          value={contactForm.phone}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              phone: e.target.value,
                            })
                          }
                          required
                        />
                      </FormField>
                      <FormField>
                        <FormLabel>Prénom et Nom</FormLabel>
                        <FormInput
                          type="text"
                          placeholder="Votre prénom et nom"
                          value={contactForm.name}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              name: e.target.value,
                            })
                          }
                          required
                        />
                      </FormField>
                    </FormRow>
                    <FormRow>
                      <FormField>
                        <FormLabel>Email</FormLabel>
                        <FormInput
                          type="email"
                          placeholder="Votre email"
                          value={contactForm.email}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              email: e.target.value,
                            })
                          }
                          required
                        />
                      </FormField>
                    </FormRow>
                  </>
                )}

                {contactForm.type === "professionnel" && (
                  <>
                    <FormRow>
                      <FormField>
                        <FormLabel>Numéro de téléphone (Obligatoire)</FormLabel>
                        <FormInput
                          type="tel"
                          placeholder="Votre numéro de téléphone"
                          value={contactForm.phone}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              phone: e.target.value,
                            })
                          }
                          required
                        />
                      </FormField>
                      <FormField>
                        <FormLabel>Prénom et Nom (Obligatoire) </FormLabel>
                        <FormInput
                          type="text"
                          placeholder="Votre nom"
                          value={contactForm.name}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              name: e.target.value,
                            })
                          }
                          required
                         
                        />
                      </FormField>
                    </FormRow>
                    <FormRow>
                      <FormField>
                        <FormLabel>Email (Optionnel)</FormLabel>
                        <FormInput
                          type="email"
                          placeholder="Votre email"
                          value={contactForm.email}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              email: e.target.value,
                            })
                          }
                        />
                      </FormField>
                      <FormRow>
                        <FormField>
                          <FormLabel>Spécialité (Obligatoire)</FormLabel>
                          <FormSelect
                            value={contactForm.specialty}
                            onChange={(e) =>
                              setContactForm({
                                ...contactForm,
                                specialty: e.target.value,
                              })
                            }
                            required
                          >
                            <option value="">
                              Sélectionnez votre spécialité
                            </option>
                            <option value="generaliste">Généraliste</option>
                            <option value="cardiologie">Cardiologie</option>
                            <option value="readaptation_cardiovasculaire">
                              Réadaptation Cardiovasculaire
                            </option>
                            <option value="chirurgie_cardio_vasculaire">
                              Chirurgie Cardio-Vasculaire
                            </option>
                            <option value="pneumologie_spirometrie">
                              Pneumologie - Spirométrie
                            </option>
                            <option value="gastro_enterologie_endoscopie_digestive">
                              Gastro-Entérologie & Endoscopie Digestive
                            </option>
                            <option value="pediatrie_endocrinologie">
                              Pédiatrie - Endocrinologie
                            </option>
                            <option value="nutrition_nutritherapie">
                              Nutrition - Nutrithérapie
                            </option>
                            <option value="diabetologie">Diabétologie</option>
                            <option value="gynecologie">Gynécologie</option>
                            <option value="anesthesie_reanimation">
                              Anesthésie & Réanimation
                            </option>
                            <option value="dermatologie">Dermatologie</option>
                            <option value="rhumatologie">Rhumatologie</option>
                            <option value="cancerologie">Cancérologie</option>
                            <option value="hematologie">Hématologie</option>
                            <option value="urologie">Urologie</option>
                            <option value="orthopedie">Orthopédie</option>
                            <option value="geriatrie">Gériatrie</option>
                            <option value="medecine_physique_reeducation">
                              Médecine Physique & Rééducation
                            </option>
                            <option value="osteopathie">Ostéopathie</option>
                            <option value="laboratoire_analyses_medicales">
                              Laboratoire d'Analyses Médicales
                            </option>
                            <option value="echographie">Échographie</option>
                            <option value="psychiatrie_addictologie">
                              Psychiatrie & Addictologie
                            </option>
                            <option value="psychologie_clinique">
                              Psychologie Clinique
                            </option>
                            <option value="therapie_capillaire_cognitivo_comportementale">
                              Thérapie Capillaire et Cognitivo-Comportementale
                            </option>
                            <option value="kinesitherapie">
                              Kinésithérapie
                            </option>
                            <option value="sport_remise_en_forme_yoga">
                              Sport de Remise en Forme / Yoga
                            </option>
                            <option value="ophtalmologie_generale">
                              Ophtalmologie générale
                            </option>
                            <option value="chirurgie_refractive">
                              Chirurgie réfractive
                            </option>
                            <option value="chirurgie_cataracte">
                              Chirurgie de la cataracte
                            </option>
                            <option value="retinologie">Rétinologie</option>
                            <option value="glaucome">Glaucome</option>
                            <option value="ophtalmologie_pediatrique">
                              Ophtalmologie pédiatrique
                            </option>
                            <option value="neuro_ophtalmologie">
                              Neuro-ophtalmologie
                            </option>
                            <option value="cornee_surface_oculaire">
                              Cornée et surface oculaire
                            </option>
                            <option value="contactologie">Contactologie</option>
                            <option value="autre">Autre spécialité</option>
                          </FormSelect>
                        </FormField>
                      </FormRow>
                    </FormRow>
                  </>
                )}

                {contactForm.type === "structure" && (
                  <>
                    <FormRow>
                      <FormField>
                        <FormLabel>Nom de la structure (Obligatoire)</FormLabel>
                        <FormInput
                          type="text"
                          placeholder="Nom de votre établissement"
                          value={contactForm.structure}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              structure: e.target.value,
                            })
                          }
                          required
                        />
                      </FormField>
                      <FormField>
                        <FormLabel>Email (Optionnel)</FormLabel>
                        <FormInput
                          type="email"
                          placeholder="Email de la structure"
                          value={contactForm.email}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              email: e.target.value,
                            })
                          }
                        />
                      </FormField>
                    </FormRow>
                    <FormRow>
                      <FormField>
                        <FormLabel>Numéro de téléphone (Obligatoire)</FormLabel>
                        <FormInput
                          type="tel"
                          placeholder="Numéro de téléphone de la structure"
                          value={contactForm.phone}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              phone: e.target.value,
                            })
                          }
                          required
                        />
                      </FormField>
                      <FormField>
                        <FormLabel>Type de structure (Obligatoire) </FormLabel>
                        <FormSelect
                          value={contactForm.structureType}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              structureType: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="">Sélectionnez le type</option>
                          <option value="cabinet">Cabinet médical</option>
                          <option value="clinique">Clinique</option>
                          <option value="hopital">Hôpital</option>
                          <option value="laboratoire">Laboratoire</option>
                          <option value="pharmacie">Pharmacie</option>
                          <option value="centre_medical">Centre médical</option>
                          <option value="autre">Autre</option>
                        </FormSelect>
                      </FormField>
                    </FormRow>
                    <FormRow>
                      <FormField>
                        <FormLabel>Pack sélectionné (Obligatoire)</FormLabel>
                        <FormSelect
                          value={contactForm.pack}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              pack: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="">Sélectionnez un pack</option>
                          <option value="Pack Essentiel">Pack Essentiel</option>
                          <option value="Pack Avancé">Pack Avancé</option>
                          <option value="Pack Téranga">Pack Téranga</option>
                          <option value="Autre">
                            Autre / Je ne sais pas encore
                          </option>
                        </FormSelect>
                      </FormField>
                    </FormRow>
                  </>
                )}

                <FormField>
                  <FormLabel>Message (Obligatoire)</FormLabel>
                  <FormTextarea
                    placeholder="Votre message"
                    value={contactForm.message}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        message: e.target.value,
                      })
                    }
                    required
                  ></FormTextarea>
                </FormField>

                {submitError && <ErrorMessage>{submitError}</ErrorMessage>}

                <CTAButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Envoi en cours..." : "Envoyer"}
                </CTAButton>
              </ContactForm>
            )}

            {/* Bouton de vérification toujours disponible même sans soumission */}
            {!submitSuccess && (
              <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
                <p
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    marginBottom: "1rem",
                  }}
                >
                  Vous avez déjà soumis une demande ?
                </p>
                <SecondaryButton onClick={() => setIsVerificationOpen(true)}>
                  Vérifier l'état de votre demande
                </SecondaryButton>
              </div>
            )}
          </CTAContent>
        </motion.div>
      </CTASection>

      {/* Modal pour afficher les détails du service */}
      <AnimatePresence>
        {activeService && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveService(null)}
          >
            <ModalContent
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: activeService.packs ? "800px" : "600px" }}
            >
              <CloseButton onClick={() => setActiveService(null)}>
                ×
              </CloseButton>
              <ModalHeader style={{ color: activeService.color }}>
                <span>{activeService.icon}</span>
                <h2>{activeService.title}</h2>
              </ModalHeader>
              <ModalBody>
                <p>{activeService.description}</p>

                {activeService.packs ? (
                  <div>
                    <h4>Nos packs de digitalisation:</h4>
                    <PacksContainer>
                      {activeService.packs.map((pack, index) => (
                        <PackCard key={index} isPopular={index === 1}>
                          {index === 1 && (
                            <PopularBadge>Populaire</PopularBadge>
                          )}
                          <PackName>{pack.name}</PackName>
                          <PackPrice>{pack.price}</PackPrice>
                          <PackFeatures>
                            {pack.features.map((feature, i) => (
                              <PackFeature key={i}>
                                <FeatureIcon
                                  style={{ color: activeService.color }}
                                >
                                  ✓
                                </FeatureIcon>
                                {feature}
                              </PackFeature>
                            ))}
                          </PackFeatures>
                          <PackButton
                            style={{ backgroundColor: activeService.color }}
                            onClick={() => handlePackSelection(pack.name)}
                          >
                            Choisir ce pack
                          </PackButton>
                        </PackCard>
                      ))}
                    </PacksContainer>
                  </div>
                ) : (
                  <>
                    <h4>Caractéristiques:</h4>
                    <FeaturesList>
                      {activeService.features.map((feature, index) => (
                        <FeatureItem key={index}>
                          <FeatureIcon style={{ color: activeService.color }}>
                            ✓
                          </FeatureIcon>
                          {feature}
                        </FeatureItem>
                      ))}
                    </FeaturesList>
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <ModalButton
                  style={{ backgroundColor: activeService.color }}
                  onClick={() => {
                    setActiveService(null);
                    scrollToSection(contactRef);
                  }}
                >
                  {activeService.packs
                    ? "Demander une démo"
                    : "Demander plus d'informations"}
                </ModalButton>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Modal pour afficher les images de la galerie */}
      <AnimatePresence>
        {activeGalleryImage && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveGalleryImage(null)}
          >
            <GalleryModalContent
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseButton onClick={() => setActiveGalleryImage(null)}>
                ×
              </CloseButton>
              <GalleryModalImage
                src={activeGalleryImage.url}
                alt={activeGalleryImage.title}
              />
              <GalleryModalInfo>
                <h3>{activeGalleryImage.title}</h3>
                <p>{activeGalleryImage.description}</p>
              </GalleryModalInfo>
            </GalleryModalContent>
          </Modal>
        )}
      </AnimatePresence>

      {/* Modal de vérification des demandes */}
      <AnimatePresence>
        {isVerificationOpen && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsVerificationOpen(false)}
          >
            <ModalContent
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -50, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "550px" }}
            >
              <CloseButton onClick={() => setIsVerificationOpen(false)}>
                ×
              </CloseButton>
              <ModalHeader
                style={{ color: "#009eff", justifyContent: "center" }}
              >
                <h2>Vérifier l'état de votre demande</h2>
              </ModalHeader>
              <ModalBody>
                {!verificationResults ? (
                  <form onSubmit={handleVerifyRequest}>
                    <FormField>
                      <FormLabel>Rechercher par</FormLabel>
                      <FormSelect
                        value={verificationData.searchType}
                        onChange={(e) =>
                          setVerificationData({
                            ...verificationData,
                            searchType: e.target.value,
                          })
                        }
                      >
                        <option value="phone">Numéro de téléphone</option>
                        <option value="name">Nom et prénom</option>
                      </FormSelect>
                    </FormField>

                    <FormField>
                      <FormLabel>
                        {verificationData.searchType === "phone"
                          ? "Votre numéro de téléphone"
                          : "Votre nom et prénom"}
                      </FormLabel>
                      <FormInput
                        type="text"
                        value={verificationData.value}
                        onChange={(e) =>
                          setVerificationData({
                            ...verificationData,
                            value: e.target.value,
                          })
                        }
                        placeholder={
                          verificationData.searchType === "phone"
                            ? "Ex: 770123456"
                            : "Ex: Jean Dupont"
                        }
                        required
                      />
                    </FormField>

                    {verificationError && (
                      <ErrorMessage>{verificationError}</ErrorMessage>
                    )}

                    <CTAButton type="submit" disabled={isVerifying}>
                      {isVerifying ? "Recherche en cours..." : "Vérifier"}
                    </CTAButton>
                  </form>
                ) : (
                  <div>
                    <h3 style={{ marginBottom: "1.5rem", fontSize: "1.2rem" }}>
                      Résultats de la recherche
                    </h3>

                    {verificationResults.map((result, index) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: "1.5rem",
                          padding: "1.5rem",
                          backgroundColor: "#F9FAFB",
                          borderRadius: "0.5rem",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        <p>
                          <strong>Date de soumission:</strong>{" "}
                          {result.createdAt}
                        </p>
                        <p>
                          <strong>Type de demande:</strong>{" "}
                          {result.type === "particulier"
                            ? "Particulier"
                            : result.type === "professionnel"
                            ? "Professionnel de santé"
                            : "Structure médicale"}
                        </p>
                        <p>
                          <strong>Statut:</strong>{" "}
                          <StatusBadge status={result.status}>
                            {result.status === "pending"
                              ? "En attente"
                              : result.status === "processed"
                              ? "Traitée"
                              : result.status === "rejected"
                              ? "Rejetée"
                              : "Inconnu"}
                          </StatusBadge>
                        </p>

                        {result.pack && (
                          <p>
                            <strong>Pack demandé:</strong> {result.pack}
                          </p>
                        )}

                        {result.message && (
                          <div style={{ marginTop: "1rem" }}>
                            <strong>Votre message:</strong>
                            <p
                              style={{
                                margin: "0.5rem 0",
                                padding: "0.75rem",
                                backgroundColor: "white",
                                borderRadius: "0.25rem",
                                border: "1px solid #E5E7EB",
                                fontSize: "0.9rem",
                              }}
                            >
                              {result.message}
                            </p>
                          </div>
                        )}

                        {result.replies && result.replies.length > 0 && (
                          <div style={{ marginTop: "1.5rem" }}>
                            <strong>Réponses:</strong>
                            <div
                              style={{
                                margin: "0.5rem 0",
                                padding: "0.5rem",
                                backgroundColor: "white",
                                borderRadius: "0.25rem",
                                border: "1px solid #E5E7EB",
                              }}
                            >
                              {result.replies.map((reply, replyIndex) => (
                                <div
                                  key={replyIndex}
                                  style={{
                                    padding: "0.75rem",
                                    marginBottom: "0.5rem",
                                    borderBottom:
                                      replyIndex < result.replies.length - 1
                                        ? "1px solid #E5E7EB"
                                        : "none",
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: "0.9rem",
                                      marginBottom: "0.25rem",
                                      color: "#4B5563",
                                    }}
                                  >
                                    <strong>{reply.sentBy}</strong> -{" "}
                                    {new Date(
                                      reply.sentAt?.toDate()
                                    ).toLocaleDateString("fr-FR", {
                                      day: "numeric",
                                      month: "long",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                    {reply.wasEdited && (
                                      <span
                                        style={{
                                          fontSize: "0.8rem",
                                          color: "#6B7280",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {" "}
                                        (modifié)
                                      </span>
                                    )}
                                  </p>
                                  <p
                                    style={{
                                      fontSize: "0.9rem",
                                      margin: "0",
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {reply.message}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    <SecondaryButton
                      onClick={() => {
                        setVerificationResults(null);
                        setVerificationData({
                          searchType: "phone",
                          value: "",
                        });
                      }}
                      style={{ marginTop: "1rem" }}
                    >
                      Nouvelle recherche
                    </SecondaryButton>
                  </div>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>

      <Footer>
        <FooterContent>
          <FooterLogo>
            <Logo>SAMD</Logo>
            <LogoText>Sen Assistance Médicale Digitale</LogoText>
            <FooterDescription>
              Solutions innovantes pour la digitalisation du secteur médical au
              Sénégal
            </FooterDescription>
            <FooterSocialIcons>
              <SocialIconLink>
                <FaFacebookF />
              </SocialIconLink>
              <SocialIconLink>
                <FaTwitter />
              </SocialIconLink>
              <SocialIconLink>
                <FaLinkedinIn />
              </SocialIconLink>
            </FooterSocialIcons>
          </FooterLogo>

          <FooterLinks>
            <FooterColumn>
              <FooterTitle>Services</FooterTitle>
              <FooterLink onClick={() => setActiveService(services[0])}>
                Secrétariat Virtuel
              </FooterLink>
              <FooterLink onClick={() => setActiveService(services[1])}>
                Prise de Rendez-vous
              </FooterLink>
              <FooterLink onClick={() => setActiveService(services[2])}>
                Digitalisation
              </FooterLink>
            </FooterColumn>

            <FooterColumn>
              <FooterTitle>Entreprise</FooterTitle>
              <FooterLink onClick={() => scrollToSection(aboutRef)}>
                À propos
              </FooterLink>
              <FooterLink onClick={() => scrollToSection(testimonialsRef)}>
                Témoignages
              </FooterLink>
              <FooterLink onClick={() => scrollToSection(galleryRef)}>
                Galerie
              </FooterLink>
              <FooterLink onClick={() => scrollToSection(faqRef)}>
                FAQ
              </FooterLink>
            </FooterColumn>

            <FooterColumn>
              <FooterTitle>Contact</FooterTitle>
              <FooterLink>samdigitale2024@gmail.com</FooterLink>
              <FooterLink>+221770977918</FooterLink>
              <FooterLink>Dakar, Sénégal</FooterLink>
            </FooterColumn>
          </FooterLinks>
        </FooterContent>

        <FooterBottom>
          <FooterCopyright>
            © {new Date().getFullYear()} Sen Assistance Médicale Digitale. Tous
            droits réservés.
          </FooterCopyright>
          <FooterSocial>
            <SocialLink>Facebook</SocialLink>
            <SocialLink>Twitter</SocialLink>
            <SocialLink>LinkedIn</SocialLink>
          </FooterSocial>
        </FooterBottom>
      </Footer>

      {/* Bouton retour en haut */}
      <BackToTopButton
        onClick={() => scrollToSection(heroRef)}
        initial={{ opacity: 0 }}
        animate={{ opacity: scrollY.get() > 300 ? 1 : 0 }}
      >
        <FaArrowUp />
      </BackToTopButton>

      {/* Bouton flottant pour prise de rendez-vous */}
      <FloatingAppointmentButton
        onClick={() => navigate("/quick")}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        title="Prendre rendez-vous"
      >
        <FaCalendarAlt />
      </FloatingAppointmentButton>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  font-family: "Poppins", sans-serif;
  color: #1f2937;
  overflow-x: hidden;
  position: relative;
`;

// Navbar
const Navbar = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 8%;
  background-color: rgba(255, 255, 255, 0.95);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);

  @media (max-width: 992px) {
    padding: 1rem 5%;
  }
`;

const NavLogo = styled.div`
  display: flex;
  align-items: center;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 992px) {
    display: none;
  }
`;

const NavLink = styled.a`
  font-size: 1rem;
  font-weight: 500;
  color: #4b5563;
  cursor: pointer;
  transition: color 0.2s ease;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: rgb(0, 158, 255);
    transition: width 0.3s ease;
  }

  &:hover {
    color: rgb(0, 158, 255);

    &:after {
      width: 100%;
    }
  }
`;

const NavLoginButton = styled.button`
  display: flex;
  align-items: center;
  background-color: #009eff;
  color: white;
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #009eff;
    transform: translateY(-2px);
  }
`;

const HeroSection = styled.section`
  display: flex;
  align-items: center;
  min-height: 100vh;
  padding: 8rem 8% 5rem;
  background: linear-gradient(rgb(1, 111, 147), #e0e0e0);
  position: relative;

  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
    padding: 8rem 5% 5rem;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;

const Logo = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  color: #009eff;
  margin-right: 1rem;
`;

const LogoText = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  color: #4b5563;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroHighlight = styled.span`
  color:rgb(242, 244, 245);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 8px;
    background-color: rgba(79, 70, 229, 0.2);
    z-index: -1;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.25rem;
  color:rgb(214, 219, 228);
  margin-bottom: 2.5rem;
  max-width: 600px;
  line-height: 1.6;

  @media (max-width: 992px) {
    margin: 0 auto 2.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 992px) {
    justify-content: center;
  }

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  background-color: #009eff;
  color: white;
  border: none;
  padding: 0.875rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    background-color:rgb(43, 45, 47);
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled.button`
  background-color: white;
  color: #009eff;
  border: 2px solidrgb(57, 61, 64);
  padding: 0.875rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color:rgb(57, 60, 65);
    transform: translateY(-2px);
  }
`;

const HeroImageWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 992px) {
    margin-top: 3rem;
    justify-content: center;
  }
`;

const HeroImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  color: #009eff;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(-50%) scale(1.2);
  }
`;

const AboutSection = styled.section`
  padding: 6rem 8%;
  background-color: white;

  @media (max-width: 992px) {
    padding: 4rem 5%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background-color: #009eff;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const AboutContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4rem;

  @media (max-width: 992px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const AboutImage = styled.img`
  width: 100%;
  max-width: 500px;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);

  @media (max-width: 992px) {
    max-width: 100%;
  }
`;

const AboutText = styled.div`
  flex: 1;

  h3 {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: #1f2937;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.7;
    color: #4b5563;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 992px) {
    text-align: center;
  }
`;

const AboutButton = styled(PrimaryButton)`
  margin-top: 1rem;
`;

const ServicesSection = styled.section`
  padding: 6rem 8%;
  background-color: #f9fafb;

  @media (max-width: 992px) {
    padding: 4rem 5%;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ServiceCard = styled(motion.div)`
  background-color: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;
`;

const ServiceIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const ServiceIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1f2937;
`;

const ServiceDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #6b7280;
  margin-bottom: 1.5rem;
`;

const LearnMore = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #009eff;
  font-size: 0.95rem;
`;

const TestimonialsSection = styled.section`
  padding: 6rem 8%;
  background-color: white;

  @media (max-width: 992px) {
    padding: 4rem 5%;
  }
`;

const TestimonialsSlider = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Testimonial = styled.div`
  background-color: #f9fafb;
  border-radius: 1rem;
  padding: 3rem;
  margin: 2rem 0;
  position: relative;
`;

const TestimonialContent = styled.div`
  text-align: center;
`;

const QuoteIcon = styled.div`
  font-size: 5rem;
  color: #e5e7eb;
  font-family: Georgia, serif;
  position: absolute;
  top: 10px;
  left: 20px;
  line-height: 1;
`;

const TestimonialText = styled.p`
  font-size: 1.25rem;
  line-height: 1.8;
  color: #4b5563;
  font-style: italic;
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
`;

const TestimonialAuthor = styled.h4`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: #1f2937;
`;

const TestimonialRole = styled.p`
  font-size: 1rem;
  color: #6b7280;
`;

// Galerie
const GallerySection = styled.section`
  padding: 6rem 8%;
  background-color: #f9fafb;

  @media (max-width: 992px) {
    padding: 4rem 5%;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 3rem;
`;

const GalleryItem = styled.div`
  position: relative;
  border-radius: 1rem;
  overflow: hidden;
  cursor: pointer;
  height: 250px;
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const GalleryOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  padding: 1.5rem;
  color: white;
  transform: translateY(20px);
  opacity: 0;
  transition: all 0.3s ease;

  ${GalleryItem}:hover & {
    transform: translateY(0);
    opacity: 1;
  }

  ${GalleryItem}:hover ${GalleryImage} {
    transform: scale(1.1);
  }
`;

const GalleryTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
`;

const GalleryModalContent = styled(motion.div)`
  background-color: white;
  border-radius: 1rem;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const GalleryModalImage = styled.img`
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
`;

const GalleryModalInfo = styled.div`
  padding: 1.5rem;

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 1rem;
    color: #6b7280;
  }
`;

// FAQ
const FAQSection = styled.section`
  padding: 6rem 8%;
  background-color: white;

  @media (max-width: 992px) {
    padding: 4rem 5%;
  }
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  margin-bottom: 1rem;
  border: 1px solid ${(props) => (props.isActive ? "#009eff" : "#E5E7EB")};
  border-radius: 0.75rem;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: #009eff;
  }
`;

const FAQQuestion = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 1.1rem;
`;

const FAQIcon = styled.span`
  color: #009eff;
  font-size: 1rem;
`;

const FAQAnswer = styled.div`
  padding: 0 1.25rem 1.25rem;
  color: #6b7280;
  line-height: 1.6;
`;

const CTASection = styled.section`
  padding: 5rem 8%;
  background: linear-gradient(135deg, #009eff 0%, #7c3aed 100%);

  @media (max-width: 992px) {
    padding: 4rem 5%;
  }
`;

const CTAContent = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2.5rem;
`;

const ContactForm = styled.form`
  text-align: left;
  background: white;
  padding: 2.5rem;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #374151;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #009eff;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #009eff;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #009eff;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
  }
`;

const CTAButton = styled.button`
  background-color: #009eff;
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  width: 100%;
  margin-top: 1rem;

  &:hover {
    background-color: #009eff;
    transform: translateY(-2px);
  }
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled(motion.div)`
  background-color: white;
  border-radius: 1rem;
  width: 100%;
  max-width: ${(props) => props.style?.maxWidth || "600px"};
  position: relative;
  overflow: hidden;
  max-height: 90vh;
  overflow-y: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  z-index: 10;

  &:hover {
    color: #1f2937;
  }
`;

const ModalHeader = styled.div`
  padding: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border-bottom: 1px solid #e5e7eb;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;

  p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: #4b5563;
    margin-bottom: 2rem;
  }

  h4 {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: #1f2937;
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  font-size: 1rem;
`;

const FeatureIcon = styled.span`
  margin-right: 0.75rem;
  font-weight: bold;
  color: ${(props) => props.style?.color || "#009eff"};
  flex-shrink: 0;
  margin-top: 0.2rem;
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    filter: brightness(110%);
  }
`;

// Styles pour les packs
const PacksContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const PackCard = styled.div`
  background-color: ${(props) => (props.isPopular ? "#F9FAFB" : "white")};
  border: 2px solid ${(props) => (props.isPopular ? "#009eff" : "#E5E7EB")};
  border-radius: 1rem;
  padding: 2rem;
  position: relative;
  transition: all 0.3s ease;
  box-shadow: ${(props) =>
    props.isPopular ? "0 10px 15px -3px rgba(0, 0, 0, 0.1)" : "none"};
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #009eff;
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.4rem 1rem;
  border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.2);
`;

const PackName = styled.h5`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
  color: #1f2937;
`;

const PackPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #009eff;
  margin-bottom: 2rem;
  text-align: center;
`;

const PackFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
  flex-grow: 1;
`;

const PackFeature = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #4b5563;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PackButton = styled.button`
  width: 100%;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${(props) => props.style?.backgroundColor || "#009eff"};

  &:hover {
    filter: brightness(110%);
    transform: translateY(-2px);
  }
`;

// Login Modal
const LoginModalContent = styled(motion.div)`
  background-color: white;
  border-radius: 1rem;
  width: 100%;
  max-width: 450px;
  position: relative;
  overflow: hidden;
  padding: 2rem;
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h2 {
    font-size: 1.8rem;
    font-weight: 700;
    margin-top: 1rem;
  }
`;

const LoginForm = styled.form``;

const LoginFormButton = styled.button`
  background-color: #009eff;
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 1rem;

  &:hover {
    background-color: #009eff;
  }
`;

const ForgotPassword = styled.div`
  text-align: right;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  color: #009eff;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const LoginDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 2rem 0;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background-color: #e5e7eb;
  }

  span {
    padding: 0 1rem;
    color: #9ca3af;
    font-size: 0.9rem;
  }
`;

const RegisterLink = styled.div`
  text-align: center;
  font-size: 0.95rem;

  a {
    color: #009eff;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// Footer
const Footer = styled.footer`
  background-color: #1f2937;
  color: white;
  padding-top: 4rem;
`;

const FooterContent = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 8% 4rem;

  @media (max-width: 992px) {
    flex-direction: column;
    padding: 0 5% 4rem;
  }
`;

const FooterLogo = styled.div`
  max-width: 300px;

  @media (max-width: 992px) {
    margin-bottom: 2rem;
    text-align: center;
    max-width: 100%;
  }
`;

const FooterDescription = styled.p`
  font-size: 0.95rem;
  color: #d1d5db;
  margin: 1rem 0 1.5rem;
  line-height: 1.6;
`;

const FooterSocialIcons = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const SocialIconLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #374151;
  color: white;
  transition: all 0.3s ease;

  &:hover {
    background-color: #009eff;
    transform: translateY(-3px);
  }
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 4rem;

  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 2rem;
  }

  @media (max-width: 576px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const FooterColumn = styled.div`
  min-width: 160px;

  @media (max-width: 992px) {
    min-width: 140px;
  }

  @media (max-width: 576px) {
    text-align: center;
  }
`;

const FooterTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: white;
`;

const FooterLink = styled.a`
  display: block;
  font-size: 0.95rem;
  color: #d1d5db;
  margin-bottom: 0.75rem;
  transition: color 0.2s ease;
  cursor: pointer;

  &:hover {
    color: #009eff;
  }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 8%;
  border-top: 1px solid #374151;

  @media (max-width: 992px) {
    padding: 1.5rem 5%;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const FooterCopyright = styled.div`
  font-size: 0.9rem;
  color: #9ca3af;
`;

const FooterSocial = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const SocialLink = styled.a`
  font-size: 0.9rem;
  color: #9ca3af;
  transition: color 0.2s ease;
  cursor: pointer;

  &:hover {
    color: white;
  }
`;

const BackToTopButton = styled(motion.button)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #009eff;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 50;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    background-color: #009eff;
  }
`;

const FloatingAppointmentButton = styled(motion.button)`
  position: fixed;
  bottom: 2rem;
  right: 0.3rem;
  width: 50px;
  height: 50px;
  top: 85%;
  border-radius: 50%;
  background-color: #009eff;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 50;
  font-size: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px) scale(1.05);
    background-color: #009eff;
  }
`;

const TestimonialType = styled.span`
  display: inline-block;
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  background-color: ${(props) => {
    switch (props.children) {
      case "particulier":
        return "#10B981";
      case "professionnel":
        return "#3B82F6";
      case "structure":
        return "#8B5CF6";
      default:
        return "#6B7280";
    }
  }};
  color: white;
  margin-top: 0.75rem;
`;

const TestimonialForm = styled.form`
  h3 {
    text-align: center;
    margin-bottom: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
  }
`;

const TestimonialModalContent = styled(motion.div)`
  background-color: white;
  border-radius: 1rem;
  width: 90%;
  max-width: 600px;
  position: relative;
  padding: 2.5rem;
  max-height: 90vh;
  overflow-y: auto;
`;

const ViewMoreButton = styled.button`
  background: none;
  border: none;
  color: #009eff;
  font-weight: 600;
  cursor: pointer;
  display: block;
  margin: 2rem auto 0;
  font-size: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const AddTestimonialButton = styled.button`
  background-color: white;
  color: #009eff;
  border: 2px solid #009eff;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  margin: 3rem auto 0;

  &:hover {
    background-color: #f3f4f6;
    transform: translateY(-2px);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem 0;
  color: #6b7280;
  font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  background-color: #fef2f2;
  border: 1px solid #fee2e2;
  padding: 1rem;
  border-radius: 0.5rem;
  margin: 1rem 0;
  font-size: 0.95rem;
`;

const SuccessMessage = styled.div`
  color: #10b981;
  background-color: #ecfdf5;
  border: 1px solid #d1fae5;
  padding: 1.5rem;
  border-radius: 0.5rem;
  margin: 1rem 0;
  font-size: 1.1rem;
  text-align: center;
  font-weight: 600;
`;

const NoTestimonialsMessage = styled.div`
  text-align: center;
  padding: 3rem 0;
  color: #6b7280;
  font-size: 1.1rem;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 2rem;
  font-size: 0.85rem;
  font-weight: 600;
  background-color: ${(props) => {
    switch (props.status) {
      case "pending":
        return "#FEF3C7";
      case "processed":
        return "#D1FAE5";
      case "rejected":
        return "#FEE2E2";
      default:
        return "#E5E7EB";
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "pending":
        return "#D97706";
      case "processed":
        return "#059669";
      case "rejected":
        return "#DC2626";
      default:
        return "#6B7280";
    }
  }};
`;

export default SenAssistanceMedicaleDigitale;
